import { Box, createStyles, Popover, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Text } from '@repo/foundations';
import { memo } from 'react';
import type { QueriedResource } from '../types';
import { ResourceListItem } from './ResourceListItem';
import { ResourcePill } from './ResourcePill';

const useStyles = createStyles((theme) => ({
	resourcesContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing['2xs'],
		alignItems: 'flex-start',
	},
	moreButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing['2xs'],
		padding: `${theme.spacing['4xs']} ${theme.spacing['2xs']}`,
		backgroundColor: theme.other.getColor('fill/tertiary/default'),
		borderRadius: theme.radius.sm,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		flexShrink: 0,
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/tertiary/hover'),
		},
	},
	popoverDropdown: {
		backgroundColor: theme.other.getColor('surface/primary/default'),
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.other.borderRadius.md,
		boxShadow: theme.shadows.md,
		padding: `${theme.other.space[2]} 0 ${theme.other.space[2]} ${theme.other.space[3]}`,
	},
	popoverContent: {
		maxHeight: '480px',
		overflowY: 'auto',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			backgroundColor: 'transparent',
			width: theme.spacing.xs,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'transparent',
			borderRadius: theme.other.borderRadius.sm,
		},
		'&:hover::-webkit-scrollbar-thumb': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
	},
}));

interface ResourceDisplayProps {
	resources: QueriedResource[];
	visibleCount: number;
}

export const ResourceDisplay = memo(
	({ resources, visibleCount }: ResourceDisplayProps) => {
		const { classes, theme } = useStyles();
		const [opened, { open, close }] = useDisclosure(false);

		const visibleResources = resources.slice(0, visibleCount);
		const hiddenResources = resources.slice(visibleCount);
		const hasMoreResources = hiddenResources.length > 0;
		const noResources = !resources || resources.length === 0;

		if (noResources) {
			return (
				<Text size="sm" color="text/secondary/default">
					No queried resources
				</Text>
			);
		}

		return (
			<Box className={classes.resourcesContainer}>
				{visibleResources.map((resource) => (
					<ResourcePill
						key={`visible-${resource.type}-${resource.name}`}
						resource={resource}
					/>
				))}

				{hasMoreResources && (
					<Popover
						opened={opened}
						width={320}
						position="bottom-start"
						withArrow
						shadow="md"
						withinPortal
						radius="md"
						classNames={{ dropdown: classes.popoverDropdown }}
						styles={{
							arrow: {
								backgroundColor: theme.other.getColor(
									'surface/primary/default'
								),
								border: `1px solid ${theme.other.getColor(
									'border/primary/default'
								)}`,
							},
						}}
					>
						<Popover.Target>
							<Box
								className={classes.moreButton}
								onMouseEnter={open}
								onMouseLeave={close}
							>
								<Text size="xs" color="text/primary/default" weight="semibold">
									+{hiddenResources.length} more
								</Text>
							</Box>
						</Popover.Target>
						<Popover.Dropdown onMouseEnter={open} onMouseLeave={close}>
							<Box className={classes.popoverContent} pr={theme.other.space[3]}>
								<Stack spacing="xs">
									<Text
										size="xs"
										color="text/secondary/default"
										weight="semibold"
										mb="xs"
									>
										Queried Resources
									</Text>
									{hiddenResources.map((resource) => (
										<ResourceListItem
											key={`hidden-${resource.type}-${resource.name}`}
											resource={resource}
										/>
									))}
								</Stack>
							</Box>
						</Popover.Dropdown>
					</Popover>
				)}
			</Box>
		);
	}
);
